import { InputSize, InputVariant } from "@galaxy-io/dls/inputs/Input";
import MultiSelectInput, {
  MultiSelectInputSize,
  MultiSelectInputVariant,
} from "@galaxy-io/dls/inputs/MultiSelectInput";
import SelectInput, {
  SelectInputSize,
  SelectInputVariant,
  type SelectOption,
} from "@galaxy-io/dls/inputs/SelectInput";
import { styled } from "@linaria/react";
import type { ReactNode } from "react";

type Choice = { value: string; label: string; icon?: ReactNode };
const Field = styled.div<{ $width: number | string }>`
  width: ${({ $width }) => (typeof $width === "number" ? `${$width}px` : $width)};
  max-width: 100%;
  min-width: 0;
  flex: 0 0 auto;
`;
const toOption = ({ value, label, icon }: Choice): SelectOption => ({
  id: value,
  label,
  leading: icon,
});

export function SelectField({
  label,
  value,
  onChange,
  options,
  disabled = false,
  search = false,
  compact = false,
  hideLabel = false,
  width = compact ? 200 : "100%",
  variant = InputVariant.BASE,
  size = compact ? InputSize.SMALL : InputSize.MEDIUM,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Choice[];
  disabled?: boolean;
  search?: boolean;
  compact?: boolean;
  hideLabel?: boolean;
  width?: number | string;
  variant?: InputVariant;
  size?: InputSize;
  placeholder?: string;
}) {
  return (
    <Field $width={width}>
      <SelectInput
        label={hideLabel ? undefined : label}
        ariaLabel={label}
        options={options.map(toOption)}
        value={value}
        onChange={(id) => onChange(id ?? "")}
        isDisabled={disabled}
        placeholder={placeholder}
        fillWidth
        isSearchable={search}
        size={SelectInputSize[size]}
        variant={SelectInputVariant[variant]}
      />
    </Field>
  );
}

export function MultiSelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  showLabel = true,
  compact = true,
  selectAll = false,
  renderSelectedContent,
  width = compact ? 200 : "100%",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Choice[];
  placeholder: string;
  disabled?: boolean;
  showLabel?: boolean;
  compact?: boolean;
  selectAll?: boolean;
  renderSelectedContent?: (items: ChoiceWithId[], placeholder: string) => ReactNode;
  width?: number | string;
}) {
  const choices = options.map(toOption);
  // A pinned command preserves GTM's "Select all" shortcut without storing a synthetic id.
  const allId = "__gtm_select_all__";
  return (
    <Field $width={width}>
      <MultiSelectInput
        label={showLabel ? label : undefined}
        ariaLabel={label}
        isDisabled={disabled}
        options={selectAll ? [{ id: allId, label: "Select all" }, ...choices] : choices}
        pinnedIds={selectAll ? [allId] : undefined}
        value={value ? value.split(",") : []}
        placeholder={placeholder}
        onChange={(ids) =>
          onChange((ids.includes(allId) ? choices.map((o) => o.id) : ids).join(","))
        }
        isClearable
        fillWidth
        isSearchable
        size={compact ? MultiSelectInputSize.SMALL : MultiSelectInputSize.MEDIUM}
        variant={MultiSelectInputVariant.BASE}
        renderValue={(items) =>
          renderSelectedContent
            ? renderSelectedContent(
                items.map((o) => ({ id: o.id, value: o.id, label: o.label, icon: o.leading })),
                placeholder,
              )
            : items.length > 1
              ? `${items.length} selected`
              : items[0]?.label || placeholder
        }
      />
    </Field>
  );
}
type ChoiceWithId = Choice & { id: string };
